import { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Checkbox, ListRow } from 'basic-components';

import { creatorSeriesActions } from 'actions/series';

import Pipi from 'primary/Pipi';

import SymbolCell from './SymbolCell';

import { When } from 'react-if';

import classNames from 'classnames';
import './SymbolRow.scss';

const emptyFun = () => {}
const EMPTY_OBJ = {};
const EMPTY_ARRAY = [];
const LARGE_COLUMN_KEYS = creatorSeriesActions.LARGE_COLUMN_KEYS
const MEDIUM_COLUMN_KEYS = creatorSeriesActions.MEDIUM_COLUMN_KEYS

const SymbolRow = ({ scrollContRef = undefined, isSelected = false, isHovered = false, isPatternSelected = false, metadata = EMPTY_OBJ, symbols = EMPTY_OBJ, source = undefined, groupName = undefined, metadataKeys = EMPTY_ARRAY, symbolKeys = EMPTY_ARRAY, orderedColumnKeys = EMPTY_ARRAY, configuredColumnKeys = EMPTY_ARRAY, userDefinedColumnKeys = EMPTY_ARRAY, userDefinedFilters = EMPTY_ARRAY, onRowHover, onRowClick, symbolIndex = undefined, disabled = false }) => {
  const getOrderedColumnValue = useCallback(({ filterKey }) => {
    if(filterKey === "Source") {
      return source
    }
    if(filterKey === "Group") {
      return groupName
    }
    return symbols[filterKey] ?? metadata[filterKey]
  }, [groupName, metadata, source, symbols]),
  filtersValues = useMemo(() => userDefinedColumnKeys.map(udf => {
    if(udf.shouldNotBeDisplayed) {
      return undefined
    }

    if(udf.type === creatorSeriesActions.FILTER_TYPE.GROUP_NAME) {
      return groupName;
    } else if(udf.type === creatorSeriesActions.FILTER_TYPE.METADATA) {
      return metadata[udf.filterKey]
    } else if(udf.type === creatorSeriesActions.FILTER_TYPE.SYMBOL) {
      return symbols[udf.filterKey]
    }
    return ""
  }), [groupName, metadata, symbols, userDefinedColumnKeys]),
  onClick = useCallback(() => onRowClick(symbolIndex), [onRowClick, symbolIndex])

  return (
    <div
        className="ng-office-app__authed__content__results__body__scroll__row-hover-target"
        onMouseEnter={event => onRowHover({ index: symbolIndex, row: event.currentTarget, symbol: { groupName, metadata, source, symbols } })}
        onMouseLeave={() => onRowHover({})}
    >
      <ListRow
          beforeChildren={(
            <Fragment key={symbolIndex}>
              <Pipi isPatternSelected={isPatternSelected}/>
              <Checkbox
                  aria-label={`${filtersValues.filter(fV => fV !== undefined).join(" ")}`}
                  checked={isSelected || isPatternSelected}
                  disabled={isPatternSelected}
                  onChange={emptyFun}
                  onClick={onClick}
              />
            </Fragment>
          )}
          className={classNames(
            "ng-office-app__authed__content__results__body__scroll__row",
            { "ng-office-app__authed__content__results__body__scroll__row--is-hovered": isHovered },
            { "ng-office-app__authed__content__results__body__scroll__row--is-selected": isSelected },
            { "ng-office-app__authed__content__results__body__scroll__row--is-disabled": disabled }
          )}
          height={32}
          offset={80 * 20} // it is also a horizontal offset - right now max 20 columns
          onClick={onClick}
          scrollContRef={scrollContRef}
    >
      <div
          className="ng-office-app__authed__content__results__body__scroll__row__inner"
      >
        {orderedColumnKeys.map(column => (
          <SymbolCell
              isLarge={LARGE_COLUMN_KEYS.includes(column.filterKey)}
              isMedium={MEDIUM_COLUMN_KEYS.includes(column.filterKey)}
              key={`ordered-${column.filterKey}`}
          >
            {getOrderedColumnValue(column)}
          </SymbolCell>
        ))}
        {symbolKeys.filter(symbolKey => !configuredColumnKeys.includes(symbolKey) && userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.SYMBOL && udf.filterKey === symbolKey) === -1).map(symbolKey => (
          <SymbolCell
              isLarge={LARGE_COLUMN_KEYS.includes(symbolKey)}
              isMedium={MEDIUM_COLUMN_KEYS.includes(symbolKey)}
              key={`symbol-${symbolKey}`}
          >
            {symbols[symbolKey]}
          </SymbolCell>
        ))}
        {metadataKeys.filter(metadataKey => !configuredColumnKeys.includes(metadataKey) && userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.METADATA && udf.filterKey === metadataKey) === -1).map(metadataKey => (
          <SymbolCell
              isLarge={LARGE_COLUMN_KEYS.includes(metadataKey)}
              isMedium={MEDIUM_COLUMN_KEYS.includes(metadataKey)}
              key={`metadata-${metadataKey}`}
          >
            {metadata[metadataKey]}
          </SymbolCell>
        ))}
        <When condition={userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.GROUP_NAME) === -1}>
          {() => (
            <SymbolCell>
              {groupName}
            </SymbolCell>
          )}
        </When>
      </div>
      </ListRow>
    </div>
  )
}

SymbolRow.propTypes = {
  configuredColumnKeys: PropTypes.array,
  disabled: PropTypes.bool,
  groupName: PropTypes.string,
  isHovered: PropTypes.bool,
  isPatternSelected: PropTypes.bool, 
  isSelected: PropTypes.bool, 
  metadata: PropTypes.object, 
  metadataKeys: PropTypes.array,
  onRowClick: PropTypes.func.isRequired,
  onRowHover: PropTypes.func.isRequired,
  orderedColumnKeys: PropTypes.array,
  scrollContRef: PropTypes.any,
  source: PropTypes.string,
  symbolIndex: PropTypes.number,
  symbolKeys: PropTypes.array,
  symbols: PropTypes.object,
  userDefinedColumnKeys: PropTypes.array,
  userDefinedFilters: PropTypes.array
}

export default SymbolRow;
